import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import {
    signInWithEmailAndPassword,
    signInWithCustomToken,
    signOut,
    onAuthStateChanged
} from 'firebase/auth';
import { FirebaseAuth } from '../firebase/auth';
import { LoginUser, StartImpersonation, StopImpersonation } from '../backend';
import { useQueryClient } from 'react-query';

const AuthContext = createContext();

const IMPERSONATION_KEY = 'pulse_impersonation';

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }) {
    const queryClient = useQueryClient();
    const [user, setUser] = useState(null);
    const [userDetails, setUserDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isImpersonating, setIsImpersonating] = useState(() => {
        try {
            const stored = sessionStorage.getItem(IMPERSONATION_KEY);
            return stored ? JSON.parse(stored).active : false;
        } catch { return false; }
    });
    const [impersonationTarget, setImpersonationTarget] = useState(() => {
        try {
            const stored = sessionStorage.getItem(IMPERSONATION_KEY);
            return stored ? JSON.parse(stored).targetUser : null;
        } catch { return null; }
    });
    const isAuthTransitioningRef = useRef(false);
    const [isAuthTransitioning, setIsAuthTransitioning] = useState(false);

    async function login(email, password) {
        try {
            setError(null);
            setLoading(true);

            // First authenticate with Firebase
            const userCredential = await signInWithEmailAndPassword(FirebaseAuth, email, password);

            // Then get user details from backend
            const response = await LoginUser({
                email: userCredential.user.email,
                uid: userCredential.user.uid
            });

            setUserDetails(response?.data?.user);
            return response;
        } catch (error) {
            setError(error.message);
            throw error;
        } finally {
            setLoading(false);
        }
    }

    async function refetchUserDetails() {
        if (user) {
            try {
                const response = await LoginUser({
                    email: user.email,
                    uid: user.uid
                });
                setUserDetails(response?.data?.user);
                return response?.data?.user;
            } catch (error) {
                console.error("Error refetching user details:", error);
            }
        }
    }

    async function startImpersonation(targetUserId, returnPath = null) {
        try {
            setLoading(true);
            setError(null);

            // Call backend to get tokens
            const response = await StartImpersonation({ targetUserId });
            const { impersonationToken, restoreToken, adminUid, targetUser } = response.data;

            // Save impersonation state to sessionStorage (including return path)
            sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify({
                active: true,
                adminUid,
                restoreToken,
                targetUser,
                returnPath,
            }));

            // Sign in as the target user
            isAuthTransitioningRef.current = true;
            setIsAuthTransitioning(true);
            queryClient.clear(); // Clear cache to prevent admin data leaking to customer dashboard
            await signInWithCustomToken(FirebaseAuth, impersonationToken);

            // State will be updated by onAuthStateChanged
            setIsImpersonating(true);
            setImpersonationTarget(targetUser);

            return targetUser;
        } catch (error) {
            isAuthTransitioningRef.current = false;
            setIsAuthTransitioning(false);
            setError(error?.response?.data?.message || error.message);
            throw error;
        } finally {
            if (!isAuthTransitioningRef.current) {
                setLoading(false);
            }
        }
    }

    async function stopImpersonation() {
        let returnPath = null;
        try {
            setLoading(true);
            setError(null);

            const stored = sessionStorage.getItem(IMPERSONATION_KEY);
            if (!stored) throw new Error('No impersonation session found');

            const { adminUid, returnPath: savedReturnPath } = JSON.parse(stored);
            returnPath = savedReturnPath;

            // Call backend to get admin restore token
            const response = await StopImpersonation({ adminUid });
            const { adminToken } = response.data;

            // Clear impersonation state
            sessionStorage.removeItem(IMPERSONATION_KEY);
            setIsImpersonating(false);
            setImpersonationTarget(null);

            // Sign back in as admin
            isAuthTransitioningRef.current = true;
            setIsAuthTransitioning(true);
            queryClient.clear(); // Clear cache to prevent customer data leaking back to admin dashboard
            await signInWithCustomToken(FirebaseAuth, adminToken);

            // Return the path so the caller can navigate back
            return returnPath;
        } catch (error) {
            // If stop fails, force clear impersonation state and sign out
            sessionStorage.removeItem(IMPERSONATION_KEY);
            setIsImpersonating(false);
            setImpersonationTarget(null);
            isAuthTransitioningRef.current = false;
            setIsAuthTransitioning(false);
            setError(error?.response?.data?.message || error.message);
            throw error;
        } finally {
            if (!isAuthTransitioningRef.current) {
                setLoading(false);
            }
        }
    }

    async function logout() {
        setError(null);

        // If currently impersonating, stop impersonation instead of signing out
        const stored = sessionStorage.getItem(IMPERSONATION_KEY);
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                if (parsed.active) {
                    const returnPath = await stopImpersonation();
                    return { wasImpersonating: true, returnPath };
                }
            } catch (e) {
                // If parsing fails, fall through to normal logout
                console.error('Error parsing impersonation data during logout:', e);
            }
        }

        // Normal logout
        setUserDetails(null);
        setIsImpersonating(false);
        setImpersonationTarget(null);
        sessionStorage.removeItem(IMPERSONATION_KEY);
        queryClient.clear(); // Ensure completely fresh state on explicit logout
        await signOut(FirebaseAuth);
        return { wasImpersonating: false };
    }

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(FirebaseAuth, async (currentUser) => {
            try {
                setLoading(true);
                setError(null);

                if (currentUser) {
                    setUser(currentUser);

                    // Get user details from backend
                    const response = await LoginUser({
                        email: currentUser.email,
                        uid: currentUser.uid
                    });
                    setUserDetails(response?.data?.user);
                } else {
                    setUser(null);
                    setUserDetails(null);
                    queryClient.clear(); // Ensure cache is wiped on session expiration
                }
            } catch (error) {
                console.error('Error fetching user details:', error);
                setError('Failed to load user details');
                // Still set the user if Firebase auth succeeded, but userDetails will be null
                if (currentUser) {
                    setUser(currentUser);
                }
            } finally {
                isAuthTransitioningRef.current = false;
                setIsAuthTransitioning(false);
                setLoading(false);
            }
        });

        return unsubscribe;
    }, []);

    const value = {
        user,
        userDetails,
        loading,
        error,
        login,
        logout,
        refetchUserDetails,
        isAuthTransitioning,
        isImpersonating,
        impersonationTarget,
        startImpersonation,
        stopImpersonation,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}